import { Card, CardContent } from "../ui/card";
import {
  CalendarIcon, Users, FolderKanban, TrendingUp,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from "recharts";

export const EkonomiTab = ({ profitData }) => {
  if (!profitData) {
    return <div className="text-center py-12 text-white/25">Laddar ekonomidata...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="flex items-center gap-6 p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/25">Intakter</p>
          <p className="stat-number text-2xl text-white">{(profitData.total_revenue / 1000).toFixed(1)}<span className="text-sm text-white/30 ml-0.5">k</span></p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/25">Kostnad</p>
          <p className="stat-number text-2xl text-red-400">{(profitData.total_cost / 1000).toFixed(1)}<span className="text-sm text-red-400/50 ml-0.5">k</span></p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/25">Vinst</p>
          <p className={`stat-number text-2xl ${profitData.total_profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
            {(profitData.total_profit / 1000).toFixed(1)}<span className="text-sm opacity-50 ml-0.5">k</span>
            <span className="text-sm ml-2 opacity-60">{profitData.total_revenue > 0 ? `${((profitData.total_profit / profitData.total_revenue) * 100).toFixed(1)}%` : "0%"}</span>
          </p>
        </div>
      </div>

      {/* Per employee profitability */}
      {profitData.by_employee?.length > 0 && (
        <Card className="bg-[#0d1218] border-white/[0.06]">
          <CardContent className="pt-5 pb-4">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#6dbf4a]" /> Lonsamhet per person
            </h3>
            <div className="space-y-2">
              {profitData.by_employee.map(emp => {
                const maxRev = Math.max(...profitData.by_employee.map(e => e.revenue), 1);
                const pct = (emp.revenue / maxRev) * 100;
                return (
                  <div key={emp.user_id} className="p-3 rounded-xl bg-[#080c12] border border-white/[0.03]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{emp.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${emp.role === "UE" ? "bg-blue-500/15 text-blue-400" : "bg-white/5 text-white/40"}`}>
                          {emp.role}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-white/25 stat-number">{emp.hours.toFixed(1)}h</span>
                        <span className="text-white stat-number">{emp.revenue.toLocaleString("sv-SE")} kr</span>
                        <span className="text-red-400/70 stat-number">-{emp.cost.toLocaleString("sv-SE")} kr</span>
                        <span className={`font-bold stat-number ${emp.profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                          {emp.profit >= 0 ? "+" : ""}{emp.profit.toLocaleString("sv-SE")} kr
                        </span>
                        <span className="text-xs text-white/20">
                          {emp.revenue > 0 ? `${((emp.profit / emp.revenue) * 100).toFixed(0)}%` : "0%"}
                        </span>
                      </div>
                    </div>
                    <div className="relative h-2 bg-white/[0.03] rounded-full overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-[#6dbf4a]/60 rounded-full" style={{ width: `${pct}%` }} />
                      <div className="absolute inset-y-0 left-0 bg-red-500/50 rounded-full" style={{ width: `${(emp.cost / maxRev) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-white/15 mt-3">Anstallda: kostnad inkl. arbetsgivaravgift 31.42% -- UE: enbart timkostnad</p>
          </CardContent>
        </Card>
      )}

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit per project */}
        {profitData.by_project?.length > 0 && (
          <Card className="bg-[#0d1218] border-white/[0.06]">
            <CardContent className="pt-5 pb-4">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <FolderKanban className="w-4 h-4 text-[#6dbf4a]" /> Vinst per projekt
              </h3>
              <div className="space-y-3">
                {profitData.by_project.map(p => {
                  const maxVal = Math.max(...profitData.by_project.map(x => x.revenue), 1);
                  const revPct = (p.revenue / maxVal) * 100;
                  const costPct = (p.cost / maxVal) * 100;
                  return (
                    <div key={p.project_id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm text-white font-medium">{p.name}</span>
                        <span className={`text-sm font-bold stat-number ${p.profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                          {p.profit >= 0 ? "+" : ""}{p.profit.toLocaleString("sv-SE")} kr
                          <span className="text-xs opacity-75 ml-1">({p.revenue > 0 ? ((p.profit / p.revenue) * 100).toFixed(0) : 0}%)</span>
                        </span>
                      </div>
                      <div className="relative h-5 bg-[#080c12] rounded-lg overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-[#6dbf4a]/70 rounded-lg" style={{ width: `${Math.max(revPct, 1)}%` }} />
                        {costPct > 0 && <div className="absolute inset-y-0 left-0 bg-red-500/50 rounded-lg" style={{ width: `${costPct}%` }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/[0.06]">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-[#6dbf4a]/70" /><span className="text-xs text-white/30">Intakt</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500/50" /><span className="text-xs text-white/30">Kostnad</span></div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profit per month */}
        {profitData.by_month?.length > 0 && (
          <Card className="bg-[#0d1218] border-white/[0.06]">
            <CardContent className="pt-5 pb-4">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-[#6dbf4a]" /> Vinst per manad
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={profitData.by_month} margin={{ left: -10, right: 10, top: 5 }} barGap={2}>
                  <XAxis dataKey="month" stroke="#aaa" fontSize={11} tickLine={false} axisLine={false}
                    tickFormatter={(v) => { const m = ['Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec']; return m[parseInt(v.split('-')[1])-1] || v; }} />
                  <YAxis tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                    contentStyle={{ backgroundColor: '#12161e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 14px' }}
                    labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}
                    formatter={(value, name) => {
                      const labels = { revenue: 'Intakt', cost: 'Kostnad', profit: 'Vinst' };
                      return [`${value.toLocaleString('sv-SE')} kr`, labels[name] || name];
                    }}
                  />
                  <Bar dataKey="revenue" fill="#6dbf4a" radius={[4, 4, 0, 0]} maxBarSize={35} />
                  <Bar dataKey="cost" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={35} opacity={0.6} />
                  <Bar dataKey="profit" radius={[4, 4, 0, 0]} maxBarSize={35}>
                    {profitData.by_month.map((entry, i) => (
                      <Cell key={i} fill={entry.profit >= 0 ? "#22c55e" : "#f97316"} opacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Yearly accumulated profit line chart */}
      {profitData.by_month?.length > 0 && (() => {
        const yearData = {};
        profitData.by_month.forEach(m => {
          const [year, month] = m.month.split('-');
          if (!yearData[year]) yearData[year] = [];
          yearData[year].push({ month: parseInt(month), profit: m.profit, revenue: m.revenue });
        });
        const yearLines = Object.entries(yearData).map(([year, months]) => {
          months.sort((a, b) => a.month - b.month);
          let cum = 0;
          return { year, data: months.map(m => { cum += m.profit; return { month: m.month, cumProfit: Math.round(cum) }; }), total: Math.round(months.reduce((s, m) => s + m.profit, 0)) };
        });
        const chartData = [];
        const monthLabels = ['','Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];
        for (let i = 1; i <= 12; i++) {
          const point = { label: monthLabels[i] };
          yearLines.forEach(yl => { const f = yl.data.find(d => d.month === i); point[`y${yl.year}`] = f ? f.cumProfit : null; });
          chartData.push(point);
        }
        const colors = ['#6dbf4a', '#3b82f6', '#f59e0b', '#ec4899'];
        return (
          <Card className="bg-[#0d1218] border-white/[0.06]">
            <CardContent className="pt-5 pb-4">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#6dbf4a]" /> Ackumulerad vinst per ar
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ left: -10, right: 10, top: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#12161e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' }}
                    formatter={(v, name) => v !== null ? [`${v.toLocaleString('sv-SE')} kr`, name.replace('y', '')] : [null, null]} />
                  {yearLines.map((yl, i) => (
                    <Line key={yl.year} type="monotone" dataKey={`y${yl.year}`} stroke={colors[i % colors.length]} strokeWidth={2.5}
                      dot={{ fill: colors[i % colors.length], r: 3 }} connectNulls={false} name={`y${yl.year}`} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/[0.06]">
                {yearLines.map((yl, i) => (
                  <span key={yl.year} className="flex items-center gap-1.5 text-xs text-white/30">
                    <div className="w-3 h-1 rounded" style={{ background: colors[i % colors.length] }} />
                    {yl.year}: {yl.total.toLocaleString('sv-SE')} kr
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};
